import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";

import { VideoTimer } from "../video-timer/video-timer.component";

import {
  MediaType,
  videoLimitAtom,
  videoLimitFinishedAtom,
} from "./media-displayer.state";

interface MediaDisplayerProps {
  media: MediaType[];
  stepIndex: number;
}
export function MediaDisplayer(props: MediaDisplayerProps): JSX.Element {
  const { media, stepIndex } = props;
  const defaultVideoLimit = window.FKL.cplQuizVideoLimit;
  const [videoLimit, setVideoLimit] = useAtom(videoLimitAtom);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const swiperRef = useRef<SwiperRef>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(videoLimit);
  const isFirstMediaVideo = media.length > 0 && !!media[0].videoUrl;
  const [videoLimitFinished, setVideoLimitFinished] = useAtom(
    videoLimitFinishedAtom
  );

  const handleTimeUpdate = (index: number) => {
    if (isFirstMediaVideo && index === 0) {
      const video = videoRefs.current[index];
      if (video) {
        const remainingTime = Math.max(0, videoLimit - video.currentTime);
        setTimeRemaining(remainingTime);
        if (remainingTime === 0) {
          setVideoLimitFinished(true);
        }
      }
    }
  };

  const handleVideoLoadedMetadata = (index: number) => {
    if (isFirstMediaVideo && index === 0) {
      const video = videoRefs.current[index];
      if (video) {
        const videoDuration = Math.floor(video.duration);
        if (videoDuration <= defaultVideoLimit) {
          setTimeRemaining(videoDuration - 1);
          setVideoLimit(videoDuration - 1);
        } else {
          setTimeRemaining(defaultVideoLimit);
          setVideoLimit(defaultVideoLimit);
        }
      }
    }
  };

  const resetSwiper = () => {
    if (swiperRef.current) {
      swiperRef.current.swiper.slideTo(0);
      swiperRef.current.swiper.update();
    }
  };

  useEffect(() => {
    if (!media[0].videoUrl) {
      setVideoLimitFinished(true);
    } else {
      setVideoLimitFinished(false);
      resetSwiper();
    }
  }, [media, setVideoLimitFinished]);

  const renderMedia = (item: MediaType, index: number) => (
    <div key={stepIndex} className="lander__cpl__quiz--media">
      {item.videoUrl ? (
        <>
          <video
            autoPlay
            muted
            loop
            playsInline
            controls
            className="lander__cpl__quiz--media-question"
            data-testid="lander__cpl__quiz--video"
            ref={(el) => (videoRefs.current[index] = el)}
            onTimeUpdate={() => handleTimeUpdate(index)}
            onLoadedMetadata={() => handleVideoLoadedMetadata(index)}
            style={{
              pointerEvents: videoLimitFinished ? "auto" : "none",
            }}
          >
            <source src={item.videoUrl} type="video/mp4" />
          </video>
          {index === 0 && !videoLimitFinished && (
            <VideoTimer
              videoLimit={videoLimit}
              videoCoundown={Math.round(timeRemaining)}
            />
          )}
        </>
      ) : (
        <img
          className="lander__cpl__quiz--media-question"
          data-testid="lander__cpl__quiz--image"
          src={item.imageUrl!}
          alt="Quiz media"
        />
      )}
      {item.logoUrl && (
        <img
          className="lander__cpl__quiz--media-logo"
          src={item.logoUrl}
          alt="Brand Logo"
        />
      )}
    </div>
  );

  if (media.length === 1) {
    return renderMedia(media[0], 0);
  }

  return (
    <Swiper
      navigation={videoLimitFinished}
      pagination={true}
      modules={[Navigation, Pagination]}
      centeredSlides={true}
      ref={swiperRef}
      spaceBetween={0}
      slidesPerView={1}
      allowSlideNext={videoLimitFinished}
      allowSlidePrev={videoLimitFinished}
    >
      {media.map((item, index) => (
        <SwiperSlide key={index}>{renderMedia(item, index)}</SwiperSlide>
      ))}
      {!videoLimitFinished && (
        <>
          <div className="swiper-button-prev swiper-button-disabled" />
          <div className="swiper-button-next swiper-button-disabled" />
        </>
      )}
    </Swiper>
  );
}
